export const GALLERY_ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
};

// Matches the 5MB ceiling enforced by the presigned POST policy in campaigns-api.
export const GALLERY_MAX_FILE_SIZE = 1024 * 1024 * 5;

// Each file costs a presign, an upload and a confirm, so cap one batch.
export const GALLERY_MAX_BATCH = 20;

export const GALLERY_MAX_DESCRIPTION_LENGTH = 1000;

/**
 * Enables Unlayer's File Manager ("Uploads" tab), the prerequisite for the
 * `userUploads` provider registered in `./unlayer-file-manager`. Spread into each
 * editor's `features` option.
 *
 * It lives here rather than beside that provider on purpose: this module imports
 * nothing from `../../api`, so `core/utils/email.js` can spread it without
 * creating an import cycle (`core/utils` -> api -> api/pages -> core/utils).
 */
export const UNLAYER_FILE_MANAGER_FEATURES = {
  userUploads: {
    enabled: true,
    search: true,
  },
};

/**
 * Shown when a presign, upload or confirm fails. Deliberately plain: the
 * underlying failure is a raw HTTP/network string, which is both cryptic and
 * alarming to a non-technical user.
 */
export const GALLERY_UPLOAD_FAILED_MESSAGE = 'Something went wrong uploading this. Try again.';

/** True when an image has no usable description, which hides it from the content agents. */
export const needsDescription = (image) => !(image?.description ?? '').trim();

/**
 * Whether to show the "No images yet" empty state rather than the grid.
 *
 * `uploadCount` is load-bearing: an in-flight upload must not read as an empty
 * library, or a brand-new user's very first upload renders no progress bar and
 * no error — the worst possible moment to hide feedback.
 */
export const isGalleryEmpty = ({ loading, total, uploadCount, search, filter }) =>
  !loading && total === 0 && uploadCount === 0 && !search && !filter;

export const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Read a selected image's pixel dimensions before uploading.
 *
 * The gallery API requires `width`/`height` at presign time (they are required
 * fields on the agent's email image block), and nothing else in the app captures
 * them today. `createImageBitmap` is injectable so this stays testable without a
 * real decoder.
 */
export const readImageDimensions = async (file, { createBitmap = globalThis.createImageBitmap } = {}) => {
  if (typeof createBitmap === 'function') {
    const bitmap = await createBitmap(file);
    const { width, height } = bitmap;
    bitmap.close?.();
    return { width, height };
  }

  // Fallback for environments without createImageBitmap.
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(objectUrl);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read the image dimensions.'));
    };
    image.src = objectUrl;
  });
};
